import React, { useEffect } from 'react'
import {
  Outlet,
  ScrollRestoration,
  useLocation,
  useNavigate
} from 'react-router'
import { MsalAuthenticationTemplate } from '@azure/msal-react'
import { useAuth } from '../../hooks/useAuth'
import { InteractionType } from '@azure/msal-browser'

interface PrivateOutletProps {
  homeUrl: string
  loadingElement: React.ReactElement
  errorElement: React.ReactElement<{
    error?: unknown
    retryLogin?: () => void
  }>
}

const PrivateOutlet: React.FC<PrivateOutletProps> = ({
  homeUrl,
  loadingElement,
  errorElement
}) => {
  const { isAuthenticated, loginRequest } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  useEffect(() => {
    if (isAuthenticated && location.pathname === '/') {
      navigate(homeUrl, { replace: true })
    }
  }, [isAuthenticated, location.pathname, navigate, homeUrl])

  const AuthenticationError = ({
    error,
    login
  }: {
    error?: Error | null
    login: (interactionType?: InteractionType) => Promise<unknown>
  }) => {
    const isTimeout =
      error != null && 'errorCode' in error && error.errorCode === 'timed_out'
    if (error) {
      console.error('MSAL authentication failed', {
        errorCode: 'errorCode' in error ? error.errorCode : undefined,
        subError: 'subError' in error ? error.subError : undefined,
        correlationId:
          'correlationId' in error ? error.correlationId : undefined,
        error
      })
    }

    return React.cloneElement(errorElement, {
      error,
      retryLogin: isTimeout
        ? () => void login(InteractionType.Redirect)
        : undefined
    })
  }

  return (
    <>
      <ScrollRestoration getKey={(location) => location.pathname} />
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        authenticationRequest={loginRequest}
        errorComponent={AuthenticationError}
        loadingComponent={() => loadingElement}
      >
        <Outlet />
      </MsalAuthenticationTemplate>
    </>
  )
}

export default PrivateOutlet
